import { State } from '@meraki-internal/state';
import { DevSettings, IDevSettingsState } from './DevSettings';
import { AppPrincipal } from '../auth/AppPrincipal';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import { DEV_SETTINGS_METADATA, DevSettingSection, SECTION_INFO } from './DevSettingsMetadata';

export interface IEnabledDevSetting {
    key: keyof IDevSettingsState;
    name: string;
    description: string;
    section: DevSettingSection;
}

export class DevFlagsPopupViewModel extends State<{ isOpen: boolean }> {
    private hasShownThisSession = false;

    static inject = () => [DevSettings, AppPrincipal];

    constructor(
        private devSettings: DevSettings,
        private principal: AppPrincipal
    ) {
        super({ isOpen: false });
    }

    checkAndShow = () => {
        // Never show more than once per session
        if (this.hasShownThisSession) {
            return;
        }

        // Never show for automated tests
        if (isAutomatedTests()) {
            return;
        }

        // Only show for admin users
        if (!this.principal.isAdmin) {
            return;
        }

        // Only show if there are enabled dev flags
        const enabledFlags = this.getEnabledDevFlags();
        if (enabledFlags.length === 0) {
            return;
        }

        this.hasShownThisSession = true;
        this.setState({ isOpen: true });
    };

    close = () => {
        this.setState({ isOpen: false });
    };

    isOpen = () => this.state.isOpen;

    getEnabledDevFlags = (): IEnabledDevSetting[] => {
        const allKeys = Object.keys(DEV_SETTINGS_METADATA) as (keyof IDevSettingsState)[];
        return allKeys
            .filter(key => this.devSettings.state[key])
            .map(key => ({
                key,
                name: DEV_SETTINGS_METADATA[key].name,
                description: DEV_SETTINGS_METADATA[key].description,
                section: DEV_SETTINGS_METADATA[key].section
            }));
    };

    getEnabledFlagsBySection = (): { section: DevSettingSection; title: string; settings: IEnabledDevSetting[] }[] => {
        const enabledFlags = this.getEnabledDevFlags();
        const sections: DevSettingSection[] = ['general', 'recording', 'tasks'];

        return sections
            .map(section => ({
                section,
                title: SECTION_INFO[section].title.replace(' Dev Settings', ''),
                settings: enabledFlags.filter(f => f.section === section)
            }))
            .filter(group => group.settings.length > 0);
    };
}
